// ./src/utils/api/EmNotificationService.ts
//
// NOTIFICATIONS — what happened, and have I seen it (ROADMAP A2b #57).
//
// ⚠⚠ THE NAME IS `emNotification*`, AND THAT IS NOT A STYLE CHOICE. This app shipped a whole
// notifications feature — a bell, an unread dot, a provider polling every 60 seconds for every
// signed-in user — that queried `userNotificationList { carId … car { id label } }`. That query is
// defined in exactly ONE repo in this org and it is **carexpenses-api**, which is not one of our
// gateway's subgraphs. It never returned anything, and nothing ever failed loudly: a query the
// gateway REJECTS looks exactly like an empty inbox. All of it was deleted in #51, and
// `npm run check-nav` still asserts the foreign shape stays gone.
// ⚠ So this file must never reintroduce those names. `Em*` is this product's own prefix, the same
// one `EmHomeAttention` uses.
//
// ⚠⚠ AND THE LESSON THAT OUTLIVES THE BUG: if a surface has been "quietly empty" for a while, CURL
// THE GATEWAY for its query before assuming the data is just missing.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

/** What happened. Mirrors `boundary/notification.ts` on the server — extend both together. */
export const NOTIFICATION_KIND = {
  GUEST_REQUEST: "guest_request",
  SPONSOR_APPLICATION: "sponsor_application",
  VOLUNTEER_APPLICATION: "volunteer_application",
  /** #170 — a vendor sent a document; it waits for the crew's review. */
  VENDOR_DOCUMENT: "vendor_document",
  /** #202 — a venue approved a vendor's document or sent it back, through its link. */
  VENUE_DOCUMENT_DECISION: "venue_document_decision",
  /** #202 — a vendor was reminded and a document is past its due date. */
  VENDOR_OVERDUE: "vendor_overdue",
  /** #202 — a vendor owes a document and has nobody to write to. */
  VENDOR_UNREACHABLE: "vendor_unreachable",
  VENDOR_EMAIL_UNDELIVERED: "vendor_email_undelivered",
  CONTACT_MESSAGE: "contact_message",
  /** #54b d — somebody wrote to an organizer page (no event; the email carries the message, Reply-To the writer). */
  ORGANIZER_MESSAGE: "organizer_message",
  MEMBERSHIP_HOUSEHOLD_CHANGED: "membership_household_changed",
  ARTIST_PENDING: "artist_pending",
  ARTIST_HOLD_EXPIRING: "artist_hold_expiring",
  REFUND_FAILED: "refund_failed",
  /**
   * DUE DATES (#57b). ⚠ The first kinds that also arrive by EMAIL — the bell says "this is
   * coming" as early as it is useful, the email goes out on the day itself.
   */
  BUDGET_DUE: "budget_due",
  BUDGET_OVERDUE: "budget_overdue",
  TASK_DUE: "task_due",
  TASK_OVERDUE: "task_overdue",
  APPOINTMENT_SOON: "appointment_soon",
  /** #68 — somebody lost something / a finder is bringing something / items kept past their day (weekly, per event). */
  LOST_REPORT: "lost_report",
  /** #246 — a guest added their own item to the sign-up list. */
  GUEST_SIGNUP_ITEM: "guest_signup_item",
  FOUND_REPORT: "found_report",
  LOST_FOUND_DUE: "lost_found_due",
} as const;

/** How loudly to render it. The SAME vocabulary the Home page's attention card uses. */
export const NOTIFICATION_SEVERITY = {
  URGENT: "urgent",
  WARN: "warn",
  INFO: "info",
} as const;

export interface EmNotification {
  id?: string;
  accountId?: string | null;
  userId?: string | null;
  eventId?: string | null;
  kind?: string | null;
  severity?: string | null;
  subjectKind?: string | null;
  subjectId?: string | null;
  /**
   * The numbers and names needed to compose the sentence.
   * ⚠ NEVER PROSE — the server does not speak English. The sentence is built client-side from
   * `kind` + this, because the product ships four languages.
   */
  payload?: Record<string, any> | null;
  /** NULL = unread. What the badge counts. */
  readAt?: string | null;
  /** Cleared without acting. ⚠ NOT the same as readAt — seen is not dealt with. */
  dismissedAt?: string | null;
  actionedAt?: string | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * ⚠ THERE IS DELIBERATELY NO `userId` HERE. The server pins the recipient to the caller and discards
 * anything a client sends, so a field for it would be a knob that silently does nothing.
 */
export interface EmNotificationFilter {
  id?: string[];
  eventId?: string[];
  kind?: string[];
  severity?: string[];
}

/** One kind, and where it reaches this person. */
export interface EmNotificationSetting {
  kind?: string | null;
  inApp?: boolean | null;
  emailDigest?: boolean | null;
  /**
   * Whether this kind can send an email AT ALL — the SERVER's answer, never derived here.
   *
   * ⚠⚠ THE SETTINGS SCREEN RENDERS A SWITCH ONLY WHERE THIS IS TRUE. Seven of the twelve kinds
   * have no sender, and a switch for one of those would store a choice nothing will ever act on —
   * the same dead-feature shape as a column no code writes. A list of "emailable kinds" on this
   * side would drift silently the first time the server gained a sender.
   */
  emailAvailable?: boolean | null;
  severity?: string | null;
  /** True when they have never chosen — the values above are the declared defaults. */
  isDefault?: boolean | null;
}

const NOTIFICATION_FIELDS = `
  id
  eventId
  kind
  severity
  subjectKind
  subjectId
  payload
  readAt
  dismissedAt
  actionedAt
  createdAt
`;

const NOTIFICATION_LIST_QUERY = gql`
  query EmNotificationList($filter: EmNotificationFilter, $params: PaginationAndSorting) {
    emNotificationList(filter: $filter, params: $params) {
      code
      total
      errors { name errors }
      data { ${NOTIFICATION_FIELDS} }
    }
  }
`;

const NOTIFICATION_UNREAD_QUERY = gql`
  query EmNotificationUnreadCount {
    emNotificationUnreadCount {
      code
      errors { name errors }
      data { unread }
    }
  }
`;

const NOTIFICATION_SETTINGS_QUERY = gql`
  query EmNotificationSettings {
    emNotificationSettings {
      code
      errors { name errors }
      data { kind inApp emailDigest emailAvailable severity isDefault }
    }
  }
`;

const NOTIFICATION_MARK_READ_MUTATION = gql`
  mutation EmNotificationMarkRead($params: EmNotificationStampInput) {
    emNotificationMarkRead(params: $params) {
      code
      errors { name errors }
      data { changed unread }
    }
  }
`;

const NOTIFICATION_DISMISS_MUTATION = gql`
  mutation EmNotificationDismiss($params: EmNotificationStampInput) {
    emNotificationDismiss(params: $params) {
      code
      errors { name errors }
      data { changed unread }
    }
  }
`;

const NOTIFICATION_SAVE_SETTING_MUTATION = gql`
  mutation EmNotificationSaveSetting($params: EmNotificationSettingInput) {
    emNotificationSaveSetting(params: $params) {
      code
      errors { name errors }
      data { kind inApp emailDigest emailAvailable isDefault }
    }
  }
`;

export class EmNotificationService {
  static async list(
    filter?: EmNotificationFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    return ApiService.executeQuery(NOTIFICATION_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /** The badge. Open AND unread — an actioned item never keeps it lit. */
  static async unreadCount(): Promise<OpResult> {
    return ApiService.executeQuery(NOTIFICATION_UNREAD_QUERY, {});
  }

  /**
   * ⚠ OMITTING `ids` MEANS ALL OF MINE — that is what a "Mark all read" button sends. It is safe
   * because the SERVER pins the recipient on the UPDATE itself, not because this client is careful.
   */
  static async markRead(ids?: string[]): Promise<OpResult> {
    return ApiService.executeMutation(NOTIFICATION_MARK_READ_MUTATION, { params: { ids: ids || [] } });
  }

  /** ⚠ Dismiss CLOSES an item; marking it read does not. They are different intentions. */
  static async dismiss(ids: string[]): Promise<OpResult> {
    return ApiService.executeMutation(NOTIFICATION_DISMISS_MUTATION, { params: { ids } });
  }

  /** Every kind with this person's resolved channels — including kinds they have never configured. */
  static async settings(): Promise<OpResult> {
    return ApiService.executeQuery(NOTIFICATION_SETTINGS_QUERY, {});
  }

  /** ⚠ Passing `null` for a channel CLEARS the override back to the default; omitting it leaves it. */
  static async saveSetting(params: { kind: string; inApp?: boolean | null; emailDigest?: boolean | null }): Promise<OpResult> {
    return ApiService.executeMutation(NOTIFICATION_SAVE_SETTING_MUTATION, { params });
  }
}
